/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectionStrategy, Component, Inject, Input, OnChanges, OnInit, SimpleChanges, computed, signal} from '@angular/core';
import * as _ from 'lodash';

import template from './notes.component.html';
import {Uuid} from '../../../../components/util/uuid';
import {formatNoteData, isEmptyNoteHtml} from './notes-commons';
import {NoteResource} from '../../../../components/api/resources/resource/note.resource';
import {UserResource} from '../../../../components/api/resources/resource/user.resource';
import {LocalUserProfile} from '../../../../components/session/local-user-profile';

interface NoteEntry {
  id: string;
  data?: string | string[];
  workingData?: string;
  updateTime?: string;
  updateUser?: {id: string; displayName?: string; name?: string};
  createUser?: {id: string; displayName?: string; name?: string};
  editThisNote?: boolean;
  $save?: (success?: (saved: NoteEntry) => void, error?: (reason: any) => void) => any;
}

interface NoteRef {
  id: string;
}

@Component({
  standalone: false,
  selector: 'adn-notes',
  host: {class: 'adn-notes'},
  template,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [`
    .note-edit-actions {
      margin-top: 10px;
    }
    .note-edit-cancel {
      margin-right: 14px;
      cursor: pointer;
    }
  `]
})
export class NotesComponent implements OnInit, OnChanges {
  @Input() object: any;
  /** Resource used for savePartial when object is a working copy without API methods. */
  @Input() saveObject: any;
  @Input() param = 'notes';
  @Input() adminUserName: string;

  @Input() set hideHeading(value: boolean | string | undefined) {
    this.hideHeadingEnabled.set(value === true || value === 'true');
  }

  @Input() set readOnly(value: boolean | string | undefined) {
    this.readOnlyEnabled.set(value === true || value === 'true');
  }

  readonly notes = signal<NoteEntry[]>([]);
  readonly hideHeadingEnabled = signal(false);
  readonly readOnlyEnabled = signal(false);
  readonly sortedNotes = computed(() => _.orderBy(this.notes(), ['updateTime'], ['desc']));

  readonly formatNoteData = formatNoteData;

  private localUser: any;

  constructor(
    @Inject(NoteResource) private Note: any,
    @Inject(LocalUserProfile) private localUserProfile: any,
    @Inject(UserResource) private User: any
  ) {
  }

  ngOnInit(): void {
    this.localUser = this.localUserProfile.get();
    this.loadNotes();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.object && this.object) {
      const previous = changes.object.previousValue;
      // modelComms publishes working-copy clones after save; same id must not
      // wipe in-progress / just-linked notes.
      if (previous?.id && previous.id === this.object.id && this.notes().length) {
        return;
      }
      this.loadNotes();
    }
  }

  addNote(): void {
    const note = this.Note.create({
      id: Uuid.generate(),
      createUser: {
        id: this.localUser.userId,
        displayName: this.localUser.displayName,
        name: this.localUser.name
      },
      editThisNote: true
    });
    this.notes.update((ns) => [...ns, note]);
  }

  startEditing(note: NoteEntry): void {
    note.workingData = formatNoteData(note.data);
    note.editThisNote = true;
    this.touchNotes();
  }

  cancelEditing(note: NoteEntry): void {
    note.workingData = undefined;
    note.editThisNote = false;
    if (!note.updateTime) {
      this.notes.update((ns) => _.reject(ns, {id: note.id}));
      return;
    }
    this.touchNotes();
  }

  deleteNote(note: NoteEntry): void {
    const saveTarget = this.getSaveTarget();
    const linkedNotes = this.rejectLinkedNote(this.object, note.id);
    if (saveTarget !== this.object && this.isNoteHost(saveTarget)) {
      saveTarget[this.param] = _.cloneDeep(linkedNotes);
    }

    if (!_.isFunction(saveTarget?.savePartial)) {
      this.notes.update((ns) => _.reject(ns, {id: note.id}));
      return;
    }

    Promise.resolve(saveTarget.savePartial(this.param)).then(() => {
      this.notes.update((ns) => _.reject(ns, {id: note.id}));
    });
  }

  updateNote(note: NoteEntry): void {
    if (isEmptyNoteHtml(note.workingData)) {
      return;
    }

    note.data = _.clone(note.workingData);
    note.workingData = undefined;
    note.editThisNote = false;
    if (!Array.isArray(note.data)) {
      note.data = [note.data as string];
    }
    this.touchNotes();

    note.$save(
      () => this.onNoteSaved(note),
      () => undefined
    );
  }

  private onNoteSaved(note: NoteEntry): void {
    this.applyLocalUserDetails(note);

    this.notes.update((ns) => {
      const index = _.findIndex(ns, ['id', note.id]);
      if (index < 0) {
        return ns;
      }
      const next = ns.slice();
      next[index] = note;
      return next;
    });

    const saveTarget = this.getSaveTarget();
    this.ensureHostId(saveTarget);
    this.ensureHostId(this.object);

    if (_.find(this.getLinkedNotes(saveTarget), ['id', note.id])) {
      return;
    }

    this.linkNoteOnHost(this.object, note.id);
    if (saveTarget !== this.object) {
      this.linkNoteOnHost(saveTarget, note.id);
    }

    if (_.isFunction(saveTarget?.savePartial) && saveTarget.id) {
      saveTarget.savePartial(this.param);
    }
  }

  private get resolvedSaveObject(): any {
    return this.saveObject;
  }

  private getSaveTarget(): any {
    if (this.isNoteHost(this.resolvedSaveObject) && _.isFunction(this.resolvedSaveObject.savePartial)) {
      return this.resolvedSaveObject;
    }
    if (this.isNoteHost(this.object) && _.isFunction(this.object.savePartial)) {
      return this.object;
    }
    return this.resolvedSaveObject || this.object;
  }

  private ensureHostId(host: any): void {
    if (!this.isNoteHost(host) || host.id) {
      return;
    }
    const id = this.object?.id || this.resolvedSaveObject?.id;
    if (id) {
      host.id = id;
    }
  }

  private linkNoteOnHost(host: any, noteId: string): void {
    if (!this.isNoteHost(host)) {
      return;
    }
    const linkedNotes = this.getLinkedNotes(host);
    if (!_.find(linkedNotes, ['id', noteId])) {
      linkedNotes.push({id: noteId});
      host[this.param] = linkedNotes;
    }
  }

  private isNoteHost(value: any): boolean {
    return value !== null && typeof value === 'object';
  }

  private getLinkedNotes(target: any): NoteRef[] {
    if (!this.isNoteHost(target)) {
      return [];
    }
    if (!target[this.param]) {
      target[this.param] = [];
    }
    return target[this.param];
  }

  private rejectLinkedNote(target: any, noteId: string): NoteRef[] {
    if (!this.isNoteHost(target)) {
      return [];
    }
    const linkedNotes = _.reject(target[this.param] || [], {id: noteId});
    target[this.param] = linkedNotes;
    return linkedNotes;
  }

  private loadNotes(): void {
    if (!this.isNoteHost(this.object) || !this.object[this.param]?.length) {
      this.notes.set([]);
      return;
    }

    this.Note.query(_.map(this.object[this.param], 'id'), (page: {results: NoteEntry[]}) => {
      this.notes.set(page.results || []);
      this.resolveNoteAuthors();
    });
  }

  private resolveNoteAuthors(): void {
    const currentNotes = this.notes();
    const userIds = _.map(currentNotes, (note) => {
      const safeUser = note.updateUser || note.createUser;
      return safeUser?.id;
    });
    const searchUserIds = _.without(userIds, this.localUser.userId);
    const userPromise = searchUserIds.length ? this.User.query(searchUserIds).$promise : Promise.resolve({results: []});

    Promise.resolve(userPromise).then((page: {results: any[]}) => {
      const users = _.keyBy(page.results, 'id');
      _.forEach(this.notes(), (note) => {
        const safeUser = note.updateUser || note.createUser;
        if (!safeUser) {
          return;
        }
        if (safeUser.id === this.localUser.userId) {
          _.set(note, ['updateUser', 'displayName'], this.localUser.displayName);
          _.set(note, ['updateUser', 'name'], this.localUser.name);
          return;
        }
        const foundUser = users[safeUser.id];
        if (foundUser) {
          _.set(note, ['updateUser', 'displayName'], foundUser.displayName);
          _.set(note, ['updateUser', 'name'], foundUser.name);
        }
      });
      // In-place author fields — notify OnPush/signal readers.
      this.touchNotes();
    });
  }

  private applyLocalUserDetails(note: NoteEntry): void {
    if (note.updateUser?.id === this.localUser.userId) {
      note.updateUser.displayName = this.localUser.displayName;
      note.updateUser.name = this.localUser.name;
    }
  }

  /** Re-emit the notes array after mutating note objects in place. */
  private touchNotes(): void {
    this.notes.update((ns) => ns.slice());
  }
}
